/*
 * UX roadmap #101: the server-side session check, kept free of Vue, Pinia and VueUse. network-utils calls it to tell
 * an expired session from a resource the member may not read, and the sync worker bundles network-utils. It used to
 * live in stores/Auth.ts, which pulled Vue's reactivity, VueUse's storage refs and the Auth store into the worker.
 * services/data/sync-worker.graph.spec.ts keeps it that way.
 */
import {
    buildStandardHeaders,
    defaultReasonForAbortingApiCall,
    isOkStatus,
    normalizeFetchParameters
} from "@/utils/network-utils";

import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

/**
 * UX roadmap #16: dispatched on `window` by the Auth store when the stored token runs out while the member is signed
 * in (its expiry timer, or the re-check when the tab is shown again). The Application store ends the session and
 * sends the member to sign-in on it. A sign-out, here or in another tab, clears the token first and never sends it.
 */
export const SESSION_EXPIRED_EVENT = 'tmi:session-expired';

/**
 * UX roadmap #63: dispatched on `window` by the Auth store when another tab removed the token this tab was signed in
 * with: a sign-out there, or that tab noticing the expiry first (`detail.expired`). VueUse has already copied the
 * removal into this tab's token ref, so neither Auth.reset() nor SESSION_EXPIRED_EVENT runs here, and the tab used to
 * keep rendering the member's private pages. The Application store ends the session and sends the member to sign-in.
 */
export const SESSION_ENDED_ELSEWHERE_EVENT = 'tmi:session-ended-elsewhere';

export type SessionEndedElsewhereDetail = { expired: boolean };

/**
 * Verify that the session id is still valid on the server
 * @returns - expected response is 200 (valid), 204 (not valid), 401 (unauthorized) or 403 (forbidden)
 */
export async function checkForValidSession( authToken: string | null ): Promise< boolean > {
    let isSessionStillValid = false;

    // If no JWT is provided, it is assumed the user is not logged in & thus the session is no longer valid
    if ( !authToken ) {
        return isSessionStillValid;
    }

    const fetchParams = normalizeFetchParameters( {
        endpoint: 'authentication/sessionsummarybyjwt',
        payload: authToken
    } );

    const headers = buildStandardHeaders( authToken );

    // Create an AbortController to short-circuit an API call that is taking too long
    const monitor = new AbortController();
    const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), fetchParams.maxApiDuration );

    try {
        const sessionResponse: Response =
            await fetch( fetchParams.uri,  {
                method: fetchParams.method,
                body: fetchParams.body,
                headers: headers,
                signal: monitor.signal
            } );

        // Clear the timeout at the earliest point to avoid false negative results
        clearTimeout( timeoutId );

        // Check if response is OK status (200-299)
        if ( isOkStatus( sessionResponse?.status ) ) {
            // Server confirmed the session is valid
            isSessionStillValid = true;
        } else {
            // Non-OK status means session is invalid
            // 401 = Unauthorized (session expired or invalid token)
            // 403 = Forbidden (token not allowed to access this endpoint - still means session is invalid)
            // 204 = No Content (session not found)
            logger.warn( `Session verification failed with status ${sessionResponse?.status} - session is invalid` );
            isSessionStillValid = false;
        }
    } catch ( err: any ) {
        // Clear the timeout at the earliest point to avoid false negative results
        clearTimeout( timeoutId );

        logger.debug( `Session verification failed: ${err.message}` );
        // Treat network failures as indeterminate so transient issues do not force logout.
        isSessionStillValid = true;
    }

    return isSessionStillValid;
}
